import React from "react";
import {DataType, Mutation, Query, QueryResponse} from "./ApiProvider/types";
import {ApiContext, CachePolicy} from "./index";


const defaultCleanUp = <T, Q>(x: T) => x as unknown as Q;

export type CleanUpFn<T, Q> = (x: DataType<T>['data']) => DataType<Q>['data'];

export interface QueryOptions {
    variables?: { [key: string]: string }
    skip?: boolean;
    cachePolicy?: CachePolicy;
}

export interface MutationOptions {
}
export interface MutateOptions<D> {
    variables?: { [key: string]: string };
    data?: D;
}


export type MutationResponse<D, T> = [(options: MutateOptions<D>) => Promise<DataType<T> | null>, {
    loading: boolean;
    data: DataType<T> | null;
}];


export const useMutation = <D, T, Q = T>(
    mutation: Mutation<T>,
    options: MutationOptions = {},
    cleanUp: CleanUpFn<T, Q> = defaultCleanUp,
): MutationResponse<D,Q> => {
    const {mutate} = React.useContext(ApiContext);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [data, setData] = React.useState<DataType<Q> | null>(null);


    return [(options: MutateOptions<D>) => {
        const data = options.data;
        const mutationCopy = {...mutation};

        if (options.variables && Object.keys(options.variables).length > 0) {
            for (const [key, val] of Object.entries(options.variables)) {
                mutationCopy.path = mutationCopy.path.replace(`<${key}>`, val);
            }
        }
        return mutate(mutationCopy, data).then(x => {
            if (x === null) setData(null);
            else {
                const _data = {...x, data: cleanUp(x.data)};
                setData(_data);
                return _data;
            }
            return x;
        }).then(x => {
            setLoading(false);
            return x;
        });
    }, {
        data,
        loading,
    }];
};


export const useQuery = <T, Q = T>(query: Query<T>, options: QueryOptions = {}, cleanUp: CleanUpFn<T, Q> = defaultCleanUp): QueryResponse<Q> => {
    const [loading, setLoading] = React.useState<boolean>(true);
    const [data, setData] = React.useState<DataType<Q> | null>(null);
    const {fetchQuery} = React.useContext(ApiContext);

    const uniqVariables = options.variables && JSON.stringify(options.variables);
    const variablesRef = React.useRef(uniqVariables);
    variablesRef.current = uniqVariables;

    const fetch = (withLoading = true, cachePolicy: CachePolicy = options.cachePolicy || "network-first") => {
        if (!options.skip) {

            setLoading(withLoading);
            const queryCopy = {...query};

            if (options.variables && Object.keys(options.variables).length > 0) {
                for (const [key, val] of Object.entries(options.variables)) {
                    queryCopy.path = queryCopy.path.replace(`<${key}>`, val);
                }
            }

            const currVariables = variablesRef.current;
            console.debug("FETCHING", queryCopy);
            fetchQuery<T>(queryCopy, cachePolicy).then(x => {
                if (currVariables === variablesRef.current) {
                    if (x === null) setData(null);
                    else setData({...x, data: cleanUp(x.data)});
                }
            }).then(() => {
                if (currVariables === variablesRef.current) {
                    setLoading(false);
                }
            });
        }
    };

    React.useLayoutEffect(() => {
        fetch();
    }, [uniqVariables, options.skip]);

    return {
        loading,
        data,
        refetch(withLoading = true) {
            return fetch(withLoading, "no-cache");
        },
    };
};

export const makeQuery = <T, Q = T>(query: Query<T>, cleanUp: CleanUpFn<T, Q> = defaultCleanUp) =>
    (options: QueryOptions = {}) => useQuery<T, Q>(query, options, cleanUp);


export const makeMutation = <D, T, Q = T>(mutation: Mutation<T>, cleanUp: CleanUpFn<T, Q> = defaultCleanUp) =>
    (options: QueryOptions = {}) => useMutation<D, T, Q>(mutation, options, cleanUp);
